import React from "react";
import {
  makeStyles,
  TextField,
  InputAdornment,
  Button,
  Backdrop,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CalendarToday from "@material-ui/icons/CalendarToday";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { selectChange, resetState } from "../../../stores/chart/logStore";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { downloadLog } from "../../../stores/chart/asyncThunk";
import { setMessages } from "../../../stores/alertSnackbarStore";
import { downloadFromResponse } from "../../../helper/fileDownload";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(3),
  },
  searchConatiner: {
    display: "flex",
  },
  search: {
    backgroundColor:'white',
  },
  action: {
    padding: theme.spacing(1),
  },
  progress: {
    padding: theme.spacing(1),
    height: 4,
  },
}));

const LogInput = React.forwardRef(({ title, value, onClick, error, disabled }, ref) => (
  <TextField
    label={title}
    variant="outlined"
    value={value}
    onClick={onClick}
    inputRef={ref}
    required
    error={error}
    disabled={disabled}
    helperText={error ? "日付を指定してください。" : ""}
    InputProps={{
      readOnly: true,
      endAdornment: (
        <InputAdornment position="end">
          <CalendarToday />
        </InputAdornment>
      ),
    }}
  />
));

const ChartLogPage = (props) => {
  const dispatch = useDispatch();
  const { startDate, endDate, search } = useSelector((state) => state.chartLog);
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const inFlight = React.useRef(false);
  const mounted = React.useRef(true);
  const handleChange = (label, event) => {
    let value = null;
    if (label === "search") {
      value = event.target.value;
    } else {
      value = event;
    }
    dispatch(selectChange({ field: label, value: value }));
  };
  const handleDownload = async () => {
    if (inFlight.current) return;
    setSubmitted(true);
    if (!startDate || !endDate) return;
    inFlight.current = true;
    setOpen(true);
    try {
      const action = await dispatch(
        downloadLog({ startDate, endDate, search })
      );
      // Redux Toolkit 1.3's unwrapResult discards rejectWithValue's payload.
      if (downloadLog.rejected.match(action)) {
        throw action.payload || { message: "ダウンロードに失敗しました。" };
      }
      if (!mounted.current) return;
      downloadFromResponse(action.payload);
      dispatch(setMessages([
        { severity: "success", message: "ダウンロードに成功しました。" },
      ]));
    } catch (error) {
      if (mounted.current) {
        dispatch(
          setMessages([
            { severity: "error", message: (error && error.message) || "ダウンロードに失敗しました。" },
          ])
        );
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setOpen(false);
    }
  };

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      dispatch(resetState());
    };
  }, [dispatch]);

  return (
    <div className={classes.root}>
      <div className={classes.searchConatiner}>
        <div className={classes.search}>
          <TextField
            label="ユーザー名"
            variant="outlined"
            value={search}
            disabled={open}
            onChange={(e) => {
              handleChange("search", e);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className={classes.search}>
          <DatePicker
            title="ログ開始日"
            dateFormat="yyyy/MM/dd"
            selected={startDate}
            onChange={(date) => handleChange("startDate", date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            maxDate={endDate}
            monthsShown={2}
            disabled={open}
            customInput={<LogInput error={submitted && !startDate} />}
          />
        </div>
        <div className={classes.search}>
          <DatePicker
            title="ログ終了日"
            dateFormat="yyyy/MM/dd"
            selected={endDate}
            onChange={(date) => handleChange("endDate", date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            maxDate={new Date()}
            monthsShown={2}
            disabled={open}
            customInput={<LogInput error={submitted && !endDate} />}
          />
        </div>
      </div>
      <div className={classes.progress}>
        <LinearProgress style={{ display: open ? "block" : "none" }} />
      </div>
      <div className={classes.action}>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          startIcon={<CloudDownloadIcon />}
          onClick={handleDownload}
          disabled={open}
        >
          ダウンロード
        </Button>
      </div>
      <Backdrop className={classes.backdrop} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};
export default ChartLogPage;
